<template>
  <div class="tc-triage-root" data-testid="draft-triage-panel">

    <!-- Period card: subheader + controls + grid + needs-input -->
    <!-- When eventsOpen, becomes a 2-column grid: [inspector] [content] -->
    <div
      class="tc-draft-period-card"
      :class="eventsOpen ? 'tc-draft-period-card--inspector-open' : ''"
    >

      <!-- Inspector occupies the left column when open. The raw draft keeps
           dismissed needs-input cards visible (reversible); the effective draft
           drives the attributed feed so resolved items surface there. -->
      <EventsInspector
        :open="eventsOpen"
        :draft="draft"
        :effective-draft="effectiveDraft"
        :highlighted-wbs="highlightedWbs"
        :calendar-from="activePeriodStart"
        :calendar-to="activePeriodEnd"
        :focused-id="focusedId"
        :confirmed-ids="confirmedEventIds"
        :dismissed-ids="dismissedEventIds"
        :escape-suppressed="anyModalOpen"
        :new-events="newEventsCount"
        @update-draft="handleUpdateDraft"
        @close="emit('toggleEvents')"
        @select-wbs="(wbs, scope) => { highlightedWbs = wbs ?? undefined; highlightScope = scope ?? 'draft'; }"
        @confirm-event="confirmEvent"
        @use-anyway="useAnyway"
        @dismiss-event="onDismissEvent"
        @attributed-ids="(ids) => { attributedEventIds = ids; }"
        @scroll-to-row="(wbs, day, scope) => onScrollToRow(wbs, day === undefined ? [] : [day], scope)"
        @select-needs-input="onPickProject"
        @select-needs-input-group="onPickProjectGroup"
        @promote-filtered="onPromoteFiltered"
        @dismiss-needs-input="onDismissNeedsInputItem"
      />

      <!-- Blocking scrim during a regenerate or the post-save refetch — covers
           the whole period card, controls included (the grid stays visible
           underneath for context). The initial load has no scrim: the grid
           shows placeholder draft rows instead. -->
      <div
        v-if="regenerating || updatingAfterSave"
        class="tc-draft-regen-overlay"
        role="status"
        aria-live="polite"
        :data-testid="updatingAfterSave ? 'draft-updating-after-save' : 'draft-regenerating'"
      >
        <div class="tc-draft-regen-panel">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span>{{ updatingAfterSave ? 'Updating timesheet' : 'Regenerating' }}</span>
          <!-- A static spinner at 49 seconds is indistinguishable from a hang.
               Elapsed time is the one thing we can say honestly without the
               server streaming its stages (#6241). -->
          <span
            v-if="waitSeconds > 0"
            class="tc-draft-regen-elapsed"
            data-testid="draft-wait-elapsed"
          >{{ waitSeconds }}s</span>
        </div>
      </div>

      <!-- Right column: the existing stack (subheader + controls + notices + grid) -->
      <div class="tc-draft-period-content">

      <!-- Subheader: period label, progress bar, Regenerate dropdown, Verify & Publish -->
      <div class="tc-triage-subheader">
        <span class="tc-draft-period-label" data-testid="subheader-period-label">{{ periodLabel }}</span>

        <div
          class="tc-triage-progress-bar"
          :style="progressBarStyle"
          data-testid="subheader-progress-bar"
        >
          <div
            class="tc-triage-progress-fill"
            role="progressbar"
            :aria-valuenow="draftedHours"
            :aria-valuemax="targetHours"
          ></div>
        </div>

        <div class="tc-triage-spacer"></div>

        <span
          v-if="saveStatusText"
          class="text-muted small me-2"
          data-testid="draft-save-status"
          aria-live="polite"
        >{{ saveStatusText }}</span>

        <!-- The last change, still reversible (#6542). Sits with the period's
             other actions and stays until something else changes the draft. The
             title names what it reverses, which the button alone cannot. -->
        <button
          v-if="undoable"
          type="button"
          class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          :title="`Undo — ${undoable.label.toLowerCase()}`"
          :aria-label="`Undo — ${undoable.label.toLowerCase()}`"
          data-testid="undo-btn"
          @click="undoLastChange"
        >
          <svg viewBox="0 0 18 18" fill="none" aria-hidden="true" class="tc-events-icon">
            <path d="M4 7h7a4 4 0 0 1 0 8H7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M7 3.5 3.5 7 7 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Undo
        </button>

        <!-- Triage toggle — opens the drawer (source events + needs-input) -->
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          :class="{ active: eventsOpen }"
          :aria-pressed="eventsOpen"
          :aria-label="triageButtonLabel"
          title="Opens the drawer showing the source events behind this draft"
          data-testid="events-toggle-btn"
          @click="emit('toggleEvents')"
        >
          <svg viewBox="0 0 18 18" fill="none" aria-hidden="true" class="tc-events-icon">
            <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Triage
          <!-- The new-events check runs after the draft renders; no scrim for it. -->
          <span
            v-if="checkingForNewEvents"
            class="spinner-border spinner-border-sm"
            aria-hidden="true"
            data-testid="triage-checking"
          ></span>
          <template v-else>
            <span
              v-if="newEventsCheckFailed"
              class="text-warning"
              title="Couldn't check for new events"
              data-testid="triage-check-failed"
            >⚠</span>
            <span
              v-if="triageCount > 0"
              class="badge rounded-pill text-bg-secondary"
              data-testid="triage-pending-count"
            >{{ triageCount }}</span>
          </template>
        </button>

        <!-- Regenerate dropdown — manual toggle; Bootstrap JS is not bundled in this app -->
        <div ref="regenRef" class="dropdown" :class="{ show: regenOpen }">
          <button
            class="btn btn-outline-secondary btn-sm dropdown-toggle"
            type="button"
            aria-haspopup="true"
            :aria-expanded="regenOpen"
            title="Regenerate drafted entries from source events"
            data-testid="regenerate-dropdown"
            :disabled="draftLoading || regenerating"
            @click="regenOpen = !regenOpen"
          >Regenerate</button>
          <ul class="dropdown-menu dropdown-menu-end" :class="{ show: regenOpen }">
            <li>
              <button
                class="dropdown-item"
                type="button"
                title="Re-runs attribution for unresolved items and new events. Rows you've already confirmed are left alone"
                data-testid="regenerate-unresolved"
                @click="handleUpdateDraft"
              >Update draft</button>
            </li>
            <li>
              <button
                class="dropdown-item tc-dropdown-item--danger"
                type="button"
                title="Discards all triage edits and regenerates every row from source events"
                data-testid="regenerate-all"
                @click="handleReDraftEverything"
              >Re-draft everything</button>
            </li>
          </ul>
        </div>

        <!-- Timesheet actions: CSV import / export (#6961) — same manual dropdown as Regenerate -->
        <div ref="actionsRef" class="dropdown" :class="{ show: actionsOpen }">
          <button
            class="btn btn-outline-secondary btn-sm"
            type="button"
            aria-haspopup="true"
            :aria-expanded="actionsOpen"
            aria-label="Timesheet actions"
            data-testid="timesheet-actions-btn"
            @click="actionsOpen = !actionsOpen"
          >⋯</button>
          <ul class="dropdown-menu dropdown-menu-end" :class="{ show: actionsOpen }">
            <li>
              <button
                class="dropdown-item"
                type="button"
                data-testid="csv-import-btn"
                :disabled="draftLoading"
                @click="actionsOpen = false; csvInput?.click()"
              >Import…</button>
            </li>
            <li>
              <button
                class="dropdown-item"
                type="button"
                data-testid="csv-export-btn"
                @click="actionsOpen = false; downloadCsv()"
              >Export</button>
            </li>
            <li><hr class="dropdown-divider"></li>
            <li>
              <button
                class="dropdown-item"
                type="button"
                data-testid="csv-template-btn"
                @click="actionsOpen = false; downloadTemplate()"
              >Download template</button>
            </li>
          </ul>
          <input
            ref="csvInput"
            type="file"
            accept=".csv,text/csv"
            class="d-none"
            data-testid="csv-import-input"
            @change="onCsvChosen"
          />
        </div>

        <button
          type="button"
          class="btn btn-primary btn-sm"
          data-testid="verify-button"
          :disabled="!canVerify"
          @click="goToVerify"
        >Verify &amp; Publish</button>
      </div>

      <!-- Phase-needed banner: shown when one or more phased rows still lack a leaf -->
      <div
        v-if="phasedNeedingLeafCount > 0"
        class="tc-triage-phase-needed-banner"
        role="alert"
        data-testid="phase-needed-banner"
      >
        {{ leafNeededText }}
      </div>

      <!-- Controls: period select -->
      <div class="tc-triage-controls">
        <div class="tc-triage-control-group">
          <label class="tc-triage-control-label" for="draft-period-select">Period</label>
          <select
            id="draft-period-select"
            class="form-select form-select-sm tc-period-select"
            data-testid="period-select"
            :value="triageStore.activePeriodEnd ?? ''"
            :disabled="periodsLoading"
            @change="onPeriodChange(($event.target as HTMLSelectElement).value)"
          >
            <option v-if="periodsLoading" value="">Loading…</option>
            <option
              v-for="p in periods"
              :key="p.endDate"
              :value="p.endDate"
            >{{ formatPeriodLabel(p) }}</option>
          </select>

          <!-- Vantagepoint's state for the period on screen, beside the picker
               that chose it. Read from the timesheet, so it is the authority;
               the picker's own option suffixes come from period config. -->
          <span
            v-if="vpStateLabel"
            class="tc-period-vp-state"
            data-testid="period-vp-state"
          >{{ vpStateLabel }}</span>
        </div>

        <!-- The way back to the classic grid while the new one beds in (ADR-0049). -->
        <div class="form-check form-switch tc-grid-view-switch mb-0">
          <input
            id="grid-view-switch"
            class="form-check-input"
            type="checkbox"
            role="switch"
            :checked="gridView === 'new'"
            data-testid="grid-view-switch"
            @change="setGridView(($event.target as HTMLInputElement).checked ? 'new' : 'classic')"
          />
          <label class="form-check-label" for="grid-view-switch">New grid</label>
        </div>
      </div>

      <div
        v-if="tsError || draftError"
        class="tc-triage-closed-notice"
        role="alert"
        data-testid="draft-error-notice"
      >
        <span v-if="tsError">Failed to load timesheet data.</span>
        <span v-else-if="draftError">Failed to load draft. Try regenerating.</span>
      </div>

      <!-- Closed-period warning: best-effort pre-warning so the user doesn't
           draft into a period VP will reject. Only fires for non-future closed
           periods (see isPeriodClosed); the save-time error is the backstop. -->
      <div
        v-if="activePeriodClosed"
        class="tc-triage-closed-notice"
        role="status"
        data-testid="period-closed-notice"
      >This period is closed in Vantagepoint — publishing will be rejected. Pick an open period above, or have it reopened in VP.</div>

      <!-- Explicit regenerate completion signal -->
      <div
        v-if="regenDoneFlash"
        class="alert alert-success py-2 px-3 mb-0"
        role="status"
        data-testid="draft-regen-done"
      >Draft updated.</div>

      <!-- Opening a period never generates a draft; the user asks for one
           (ADR-0031). Generating keeps their own entries. -->
      <div
        v-if="draft && !draft.generatedAt"
        class="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 px-3 mb-0"
        role="status"
        data-testid="draft-not-generated"
      >
        <span>No draft for this period yet.</span>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          data-testid="generate-draft-btn"
          @click="handleUpdateDraft"
        >Generate draft</button>
      </div>

      <!-- Grid: merged VP rows + draft rows -->
      <TimesheetGrid
        v-if="gridView === 'new'"
        :vp-rows="vpRows"
        :period-start="activePeriodStart"
        :period-end="activePeriodEnd"
        :draft-rows="effectiveDraft ?? undefined"
        :draft-loading="draftLoading"
        :highlight-wbs="highlightedWbs"
        :highlight-scope="highlightScope"
        :highlight-cell="highlightedCell"
        :regenerating-event-ids="regeneratingNoteEventIds"
        :wbs1-label="tsSettings.wbsLabel(1)"
        :wbs2-label="tsSettings.wbsLabel(2)"
        :wbs3-label="tsSettings.wbsLabel(3)"
        :show-wbs2="tsSettings.showWbs2.value"
        :show-wbs3="tsSettings.showWbs3.value"
        :show-firm-name="tsSettings.showFirmName.value"
        :non-working-days="effectiveDraft?.nonWorkingDays"
        data-testid="draft-preview-grid"
        @row-click="onRowClick"
        @project-cell-click="onProjectCellClick"
        @cell-badge-click="onCellBadgeClick"
        @phase-cell-click="onPhaseCellClick"
        @row-remove="rowRemoveKey = $event"
      />
      <TimesheetPreview
        v-else
        :vp-rows="vpRows"
        :period-start="activePeriodStart"
        :period-end="activePeriodEnd"
        :draft-rows="effectiveDraft ?? undefined"
        :draft-loading="draftLoading"
        :highlight-wbs="highlightedWbs"
        :highlight-scope="highlightScope"
        :highlight-cell="highlightedCell"
        :regenerating-event-ids="regeneratingNoteEventIds"
        :wbs1-label="tsSettings.wbsLabel(1)"
        :wbs2-label="tsSettings.wbsLabel(2)"
        :wbs3-label="tsSettings.wbsLabel(3)"
        :show-wbs2="tsSettings.showWbs2.value"
        :show-wbs3="tsSettings.showWbs3.value"
        :non-working-days="effectiveDraft?.nonWorkingDays"
        data-testid="draft-preview-grid"
        @row-click="onRowClick"
        @project-cell-click="onProjectCellClick"
        @cell-badge-click="onCellBadgeClick"
        @phase-cell-click="onPhaseCellClick"
        @row-remove="rowRemoveKey = $event"
      />

      <!-- Manual add — log time to any project on any day (B.8), and rebuild the
           period from one already filed (#6541). Disabled until the draft
           arrives (ADR-0031 amendment 2026-09-28). -->
      <div class="tc-draft-add-time d-flex gap-2">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          data-testid="add-time-btn"
          :disabled="draftLoading"
          @click="openAddWizard"
        >+ Add</button>
        <button
          v-if="copyablePeriods.length > 0"
          type="button"
          class="btn btn-outline-secondary btn-sm"
          data-testid="copy-from-period-btn"
          :disabled="draftLoading"
          @click="openCopyFromPeriod"
        >Copy from…</button>
      </div>

      <!-- Freshness footer: "drafted Nm ago · trigger" -->
      <div
        v-if="freshnessLine"
        class="tc-draft-freshness-footer"
        data-testid="draft-freshness-footer"
        aria-live="polite"
      >{{ freshnessLine }}</div>

      </div><!-- end tc-draft-period-content -->
    </div>

    <!-- Row remove confirm. Undo would put the row back, but a whole row of
         hours is worth one question first. -->
    <ConfirmDialog
      :open="!!rowRemoveKey"
      title="Delete this row?"
      confirm-label="Delete"
      :destructive="true"
      :on-confirm="confirmRowRemove"
      @close="rowRemoveKey = null"
    />

    <!-- Re-draft confirm dialog -->
    <ConfirmDialog
      :open="confirmOpen"
      title="Re-draft everything"
      message="This will discard the current draft and regenerate all rows from scratch. Any manual adjustments will be lost."
      confirm-label="Re-draft"
      :destructive="true"
      :on-confirm="handleReDraftConfirm"
      @close="confirmOpen = false"
    />

    <!-- Cell detail modal (B.5) — note + reasoning + events + change project -->
    <CellDetailModal
      :open="cellDetailOpen"
      :target="cellDetailCtx?.target ?? null"
      :day-label="cellDetailCtx?.dayLabel ?? ''"
      :hours="cellDetailCtx?.hours ?? 0"
      :note="cellDetailNote"
      :note-kind="cellDetailCtx?.noteKind ?? 'agent'"
      :reasoning="cellDetailCtx?.reasoning ?? ''"
      :attribution-source="cellDetailCtx?.attributionSource ?? ''"
      :less-sure="cellDetailCtx?.lessSure ?? []"
      :confidence="cellDetailCtx?.confidence"
      :confirmed="cellDetailCtx?.confirmed"
      :events="cellDetailEvents"
      :regenerating="regeneratingNote"
      :regenerate-error="regenerateError"
      :is-phased="cellDetailCtx?.isPhased"
      :is-new="cellDetailCtx?.isNew"
      :hours-increment="tsSettings.hoursIncrement.value"
      :timesheet-rows="vpRows"
      :comment-required="commentRequired"
      :recent="recentTargets"
      :ranked-target-ids="effectiveDraft?.rankedTargetIds ?? []"
      :categories="effectiveDraft?.categories ?? []"
      :copy-sources="cellDetailCtx?.copySources ?? []"
      @regenerate="onRegenerateNote"
      @save="onCellDetailSave"
      @remove="onCellDetailRemove"
      @copy-from="copyCellFrom"
      @close="cellDetailOpen = false; pendingCellCopy = null"
    />

    <!-- A cell copy landing on a day that already holds hours. Both the hours
         and the comment go, which Undo restores but the click doesn't show. -->
    <ConfirmDialog
      :open="!!pendingCellCopy"
      title="Replace"
      message="Replace existing entry?"
      confirm-label="Replace"
      :destructive="true"
      :on-confirm="confirmCellCopy"
      @close="pendingCellCopy = null"
    />

    <!-- Attribution wizard (ADR-0042) — the single progressive picker: creates a
         new row (+ Add), resolves needs-input items, and edits an existing row's
         project / phase / task. Project → Phase → Task → Comment, two layouts. -->
    <AttributionWizard
      :open="wizardOpen"
      :mode="wizardCtx?.mode ?? 'create'"
      :context-label="wizardCtx?.contextLabel ?? 'Add time'"
      :initial-target="wizardCtx?.initialTarget ?? null"
      :initial-comment="wizardCtx?.initialComment ?? ''"
      :comment-hint="wizardCtx?.commentHint ?? ''"
      :facts="wizardCtx?.facts ?? null"
      :link="wizardCtx?.link ?? null"
      :events="wizardCtx?.events ?? []"
      :period-start="activePeriodStart"
      :period-end="activePeriodEnd"
      :timesheet-rows="vpRows"
      :recent="wizardCtx?.recent ?? recentTargets"
      :suggested="wizardCtx?.suggested ?? []"
      :ranked-target-ids="effectiveDraft?.rankedTargetIds ?? []"
      :categories="effectiveDraft?.categories ?? []"
      :hours-increment="tsSettings.hoursIncrement.value"
      :non-working-days="effectiveDraft?.nonWorkingDays"
      @confirm="onWizardConfirm"
      @cancel="onWizardCancel"
    />

    <!-- A repeat landing on days the target already has hours on. Named before
         anything is written, with both ways out (#6540). -->
    <ConfirmDialog
      :open="repeatClashOpen"
      title="Some days already have hours"
      :message="`${repeatClashes.length} of the days you picked already carry time on this project.`"
      confirm-label="Skip"
      secondary-label="Replace"
      :on-confirm="() => applyRepeat('skip')"
      :on-secondary="() => applyRepeat('replace')"
      @close="cancelRepeat"
    >
      <template #details>
        <ul class="tc-bulk-confirm-list" data-testid="repeat-clash-list">
          <li
            v-for="c in repeatClashes"
            :key="c.dayIndex"
            class="tc-bulk-confirm-row"
            data-testid="repeat-clash-row"
          >
            <span class="tc-bulk-confirm-days">{{ c.dayLabel }}</span>
            <span class="tc-bulk-confirm-hours">{{ c.hours.toFixed(2) }}h</span>
          </li>
        </ul>
      </template>
    </ConfirmDialog>

    <!-- Rebuild this period from one already filed (#6541). The source sheet's
         rows land on this period's matching weekdays; days it already carries
         hours on are named before anything is written. -->
    <ConfirmDialog
      :open="copyOpen"
      title="Copy from another period"
      :confirm-label="copyClashes.length > 0 ? 'Skip existing' : 'Copy'"
      :secondary-label="copyClashes.length > 0 ? 'Replace all' : undefined"
      :on-confirm="() => applyCopy('skip')"
      :on-secondary="() => applyCopy('replace')"
      @close="cancelCopy"
    >
      <template #details>
        <div class="tc-copy-fields">
          <div>
            <select
              class="form-select form-select-sm"
              aria-label="Period to copy from"
              data-testid="copy-source-period"
              :value="copySourcePeriodEnd"
              @change="copySourcePeriodEnd = ($event.target as HTMLSelectElement).value"
            >
              <option
                v-for="p in copyablePeriods"
                :key="p.endDate"
                :value="p.endDate"
              >{{ formatPeriodLabel(p) }}</option>
            </select>
<!--            <div class="tc-copy-hint">Rows land on this period's matching weekdays.</div>-->
          </div>

          <div v-if="copySourceHasLeaveRows" class="form-check mb-0">
            <input
              id="copy-exclude-leave"
              v-model="copyExcludeLeave"
              class="form-check-input"
              type="checkbox"
              data-testid="copy-exclude-leave"
            />
            <label class="form-check-label" for="copy-exclude-leave">Exclude Holiday / PTO rows</label>
          </div>

          <div class="tc-copy-summary" data-testid="copy-preview">
            <span class="tc-copy-summary-label fw-bold">Copying</span>
            <span v-if="copySourceLoading">Reading that period…</span>
            <span v-else-if="copyPlan.length === 0">Nothing to copy</span>
            <span v-else>{{ copyPlan.length }} {{ copyPlan.length === 1 ? 'entry' : 'entries' }} · {{ copyTotalHours.toFixed(2) }}h</span>
          </div>

          <!-- What the copy lands on top of, per project. Skip and Replace act
               on the whole copy, so this is the scale of the overwrite, not a
               per-cell inventory. -->
          <div v-if="copyClashes.length > 0">
            <div class="tc-copy-summary" data-testid="copy-clash-summary">
              <span class="tc-copy-summary-label fw-bold">Existing</span>
              <span>{{ copyClashCount }} {{ copyClashCount === 1 ? 'entry' : 'entries' }} · {{ copyClashHours.toFixed(2) }}h</span>
            </div>
            <ul class="tc-copy-clash-list" data-testid="copy-clash-list">
              <li
                v-for="c in copyClashes"
                :key="c.label"
                class="tc-bulk-confirm-row"
                data-testid="copy-clash-row"
              >
                <span class="tc-bulk-confirm-project">{{ c.label }}</span>
                <span class="tc-bulk-confirm-days">{{ c.days }} {{ c.days === 1 ? 'day' : 'days' }}</span>
                <span class="tc-bulk-confirm-hours">{{ c.hours.toFixed(2) }}h</span>
              </li>
            </ul>
          </div>

          <div v-if="copyTargetHasVpHours" class="tc-copy-hint" data-testid="copy-vp-notice">
            This period already holds hours in Vantagepoint. Copied rows add to them.
          </div>
        </div>
      </template>
    </ConfirmDialog>

    <!-- CSV import (#6961). Laid out like Copy from: what lands, what it lands
         on, and — unlike a copy — what is left alone because Vantagepoint
         already holds it (ADR-0041): the file's own vp rows, and draft rows
         landing on VP-saved cells. Then the rows the server rejected. -->
    <ConfirmDialog
      :open="importOpen"
      title="Import CSV"
      :confirm-label="importFileError ? 'Close' : importPlan.existing.length > 0 ? 'Skip existing' : 'Import'"
      :secondary-label="!importFileError && importPlan.existing.length > 0 ? 'Replace all' : undefined"
      :actions-disabled="importLoading"
      :on-confirm="() => applyImportPlan('skip')"
      :on-secondary="() => applyImportPlan('replace')"
      @close="cancelImport"
    >
      <template #details>
        <div class="tc-copy-fields">
          <div v-if="importFileError" class="tc-copy-hint text-danger" data-testid="import-file-error">{{ importFileError }}</div>
          <template v-else>
            <div class="tc-copy-summary" data-testid="import-preview">
              <span class="tc-copy-summary-label fw-bold">Importing</span>
              <span v-if="importLoading">Reading the file…</span>
              <span v-else-if="importPlan.importing.length === 0">Nothing to import</span>
              <span v-else>{{ importPlan.importing.length }} {{ importPlan.importing.length === 1 ? 'entry' : 'entries' }} · {{ importTotalHours.toFixed(2) }}h</span>
            </div>

            <div v-if="importPlan.existing.length > 0">
              <div class="tc-copy-summary" data-testid="import-clash-summary">
                <span class="tc-copy-summary-label fw-bold">Existing</span>
                <span>{{ importClashCount }} {{ importClashCount === 1 ? 'entry' : 'entries' }} · {{ importClashHours.toFixed(2) }}h</span>
              </div>
              <ul class="tc-copy-clash-list">
                <li v-for="c in importPlan.existing" :key="c.key" class="tc-bulk-confirm-row" data-testid="import-clash-row">
                  <span class="tc-bulk-confirm-project">{{ c.label }}</span>
                  <span class="tc-bulk-confirm-days">{{ c.days }} {{ c.days === 1 ? 'day' : 'days' }}</span>
                  <span class="tc-bulk-confirm-hours">{{ c.hours.toFixed(2) }}h</span>
                </li>
              </ul>
            </div>

            <div v-if="importPlan.inVp.length > 0">
              <div class="tc-copy-summary" data-testid="import-in-vp-summary">
                <span class="tc-copy-summary-label fw-bold">Already in Vantagepoint</span>
                <span>{{ importPlan.inVp.length }} skipped — change these in Vantagepoint</span>
              </div>
              <ul class="tc-copy-clash-list">
                <li v-for="r in importPlan.inVp" :key="r.line" class="tc-bulk-confirm-row" data-testid="import-in-vp-row">
                  <span class="tc-bulk-confirm-project">{{ r.label }} · {{ r.date }}</span>
                  <span class="tc-bulk-confirm-hours">file {{ r.fileHours.toFixed(2) }}h · VP {{ r.vpHours.toFixed(2) }}h</span>
                </li>
              </ul>
            </div>

            <div v-if="importIgnoredVpRows > 0" class="tc-copy-hint" data-testid="import-vp-rows-ignored">
              {{ importIgnoredVpRows }} Vantagepoint {{ importIgnoredVpRows === 1 ? 'row' : 'rows' }} ignored — change these in Vantagepoint
            </div>

            <div v-if="importErrors.length > 0">
              <div class="tc-copy-summary" data-testid="import-error-summary">
                <span class="tc-copy-summary-label fw-bold">Errors</span>
                <span>{{ importErrors.length }} {{ importErrors.length === 1 ? 'row' : 'rows' }} not imported</span>
              </div>
              <ul class="tc-copy-clash-list">
                <li v-for="(e, i) in importErrors" :key="i" class="tc-bulk-confirm-row" data-testid="import-error-row">
                  <span class="tc-bulk-confirm-project">Line {{ e.line }}: {{ e.message }}</span>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </template>
    </ConfirmDialog>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import TimesheetPreview from './TimesheetPreview.vue';
import TimesheetGrid from './TimesheetGrid.vue';
import CellDetailModal from './CellDetailModal.vue';
import AttributionWizard from './AttributionWizard.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import EventsInspector from './EventsInspector.vue';
import { queuedNeedsInput } from './needsInputGroups';
import { useDraftQueries } from '../composables/useDraftQueries';
import { useTriageActions } from '../composables/useTriageActions';
import { useTriageKeyboard } from '../composables/useTriageKeyboard';
import { useCsvTransfer } from '../composables/useCsvTransfer';
import { useTimesheetSettings } from '../composables/useTimesheetSettings';
import { useGridView } from '../composables/useGridView';

const props = defineProps<{
  eventsOpen: boolean;
  // Set while /home is showing the post-save flash, so the scrim can cover the
  // refetch that swaps the pre-save rows for what Vantagepoint now holds.
  justSaved?: boolean;
}>();

const emit = defineEmits<{
  toggleEvents: [];
}>();

const router = useRouter();

const { view: gridView, setView: setGridView } = useGridView();

const confirmOpen = ref(false);
const highlightedWbs = ref<string | undefined>(undefined);
// Which block the highlight belongs to. A card in Published / Duplicate points
// at the VP row that carries its hours, not at a draft row (#6487).
const highlightScope = ref<'draft' | 'vp'>('draft');

// Regenerate and timesheet-actions dropdowns — manual open state + outside-click
// close (Bootstrap JS not bundled)
const regenRef = ref<HTMLElement | null>(null);
const regenOpen = ref(false);
const actionsRef = ref<HTMLElement | null>(null);
const actionsOpen = ref(false);

function onDropdownOutsideClick(e: MouseEvent): void {
  const target = e.target as Node;
  if (regenRef.value && !regenRef.value.contains(target)) regenOpen.value = false;
  if (actionsRef.value && !actionsRef.value.contains(target)) actionsOpen.value = false;
}

watch(() => regenOpen.value || actionsOpen.value, (isOpen) => {
  if (isOpen) {
    document.addEventListener('mousedown', onDropdownOutsideClick);
  } else {
    document.removeEventListener('mousedown', onDropdownOutsideClick);
  }
});

onUnmounted(() => {
  document.removeEventListener('mousedown', onDropdownOutsideClick);
});

const {
  triageStore,
  activePeriodStart,
  activePeriodEnd,
  tsError,
  vpRows,
  periodsLoading,
  periods,
  commentRequired,
  regenerating,
  draftLoading,
  refreshing,
  draftError,
  draft,
  effectiveDraft,
  draftedHours,
  targetHours,
  checkingForNewEvents,
  newEventsCheckFailed,
  newEventsCount,
  saveStatusText,
  progressBarStyle,
  phasedNeedingLeafCount,
  leafNeededText,
  canVerify,
  freshnessLine,
  vpStateLabel,
  activePeriodClosed,
  formatPeriodLabel,
  periodLabel,
  onPeriodChange,
  onUpdateDraft,
  doReDraft,
} = useDraftQueries();

const {
  onRowClick,
  onPhaseCellClick,
  onProjectCellClick,
  confirmEvent,
  undoDismiss,
  useAnyway,
  dismissEvent,
  cellDetailOpen,
  cellDetailCtx,
  cellDetailNote,
  cellDetailEvents,
  regeneratingNote,
  regenerateError,
  onCellBadgeClick,
  copyCellFrom,
  pendingCellCopy,
  confirmCellCopy,
  onRegenerateNote,
  onCellDetailSave,
  onCellDetailRemove,
  removeRow,
  onPickProject,
  onPromoteFiltered,
  onDismissNeedsInputItem,
  regeneratingNoteEventIds,
  recentTargets,
  wizardOpen,
  wizardCtx,
  justAddedRow,
  openAddWizard,
  onWizardConfirm,
  openGroupWizard: onPickProjectGroup,
  onWizardCancel,
  repeatClashOpen,
  repeatClashes,
  applyRepeat,
  cancelRepeat,
  copyOpen,
  copyablePeriods,
  copySourcePeriodEnd,
  copyExcludeLeave,
  copySourceHasLeaveRows,
  copySourceLoading,
  copyPlan,
  copyTotalHours,
  copyClashes,
  copyClashCount,
  copyClashHours,
  copyTargetHasVpHours,
  openCopyFromPeriod,
  applyCopy,
  cancelCopy,
  applyImport,
  undoable,
  undoLastChange,
  forgetUndo,
} = useTriageActions({
  effectiveDraft: () => effectiveDraft.value,
  rawDraft: () => draft.value,
  activePeriodStart: () => activePeriodStart.value,
  activePeriodEnd: () => activePeriodEnd.value,
  periods: () => periods.value,
  highlightedWbs,
  commentRequired: () => commentRequired.value,
});

const {
  downloadCsv,
  downloadTemplate,
  importOpen,
  importLoading,
  importFileError,
  importPlan,
  importErrors,
  importIgnoredVpRows,
  importTotalHours,
  importClashCount,
  importClashHours,
  importFile,
  applyImportPlan,
  cancelImport,
} = useCsvTransfer({
  effectiveDraft: () => effectiveDraft.value,
  vpRows: () => vpRows.value,
  activePeriodStart: () => activePeriodStart.value,
  activePeriodEnd: () => activePeriodEnd.value,
  applyImport,
});

const csvInput = ref<HTMLInputElement | null>(null);

// Cleared after reading so choosing the same file again still fires change.
function onCsvChosen(e: Event): void {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (file) void importFile(file);
}

// The row the user asked to remove, held while the confirm is open.
const rowRemoveKey = ref<string | null>(null);

function confirmRowRemove(): void {
  if (rowRemoveKey.value) removeRow(rowRemoveKey.value);
}

// Tenant settings — drives WBS column labels, hidden columns, and the hours step.
const tsSettings = useTimesheetSettings();

// ---------------------------------------------------------------------------
// Regenerate orchestration — every regen path runs behind the full-card scrim
// and resolves into an explicit "Draft updated." signal.
// ---------------------------------------------------------------------------
const regenDoneFlash = ref(false);

// After a save the caches are invalidated but still hold the pre-save data, so
// without this the user lands on /home, reads the old rows, and watches them
// swap a second later. isLoading is false on a refetch, so the draft
// placeholders do not cover it — same reason `regenerating` exists (#6487).
const updatingAfterSave = computed(() => !!props.justSaved && refreshing.value);
let regenFlashTimer: ReturnType<typeof setTimeout> | null = null;

// Elapsed seconds on the scrim, from 5s in — before that the count is noise on
// a wait nobody notices.
const WAIT_VISIBLE_AFTER_S = 5;
const waitSeconds = ref(0);
let waitTimer: ReturnType<typeof setInterval> | null = null;

watch(regenerating, (waiting) => {
  if (waitTimer) clearInterval(waitTimer);
  waitSeconds.value = 0;
  if (!waiting) return;
  let elapsed = 0;
  waitTimer = setInterval(() => {
    elapsed += 1;
    waitSeconds.value = elapsed >= WAIT_VISIBLE_AFTER_S ? elapsed : 0;
  }, 1000);
}, { immediate: true });

onUnmounted(() => {
  if (waitTimer) clearInterval(waitTimer);
});

async function runRegen(fn: () => Promise<void>): Promise<void> {
  regenDoneFlash.value = false;
  try {
    await fn();
  } finally {
    regenDoneFlash.value = true;
    if (regenFlashTimer) clearTimeout(regenFlashTimer);
    regenFlashTimer = setTimeout(() => { regenDoneFlash.value = false; }, 4000);
  }
}

onUnmounted(() => {
  if (regenFlashTimer) clearTimeout(regenFlashTimer);
});

// Local regen dropdown handlers — close dropdown, then delegate to composable
async function handleUpdateDraft(): Promise<void> {
  regenOpen.value = false;
  await runRegen(onUpdateDraft);
}

function handleReDraftEverything(): void {
  regenOpen.value = false;
  confirmOpen.value = true;
}

// Not awaited: the dialog would hold its busy spinner over the scrim. The
// scrim is the progress signal, so the dialog closes on confirm.
function handleReDraftConfirm(): void {
  void runRegen(doReDraft);
}

// ---------------------------------------------------------------------------
// Per-event overlay state for the inspector cards (confirmed / dismissed sets)
// ---------------------------------------------------------------------------
const confirmedEventIds = computed((): Set<string> => {
  const s = new Set<string>();
  for (const [id, d] of triageStore.drafts) if (d.status === 'confirmed') s.add(id);
  return s;
});
const dismissedEventIds = computed((): Set<string> => {
  const s = new Set<string>();
  for (const [id, d] of triageStore.drafts) if (d.status === 'rejected') s.add(id);
  return s;
});

// The drawer's Pending count plus the events the draft hasn't seen yet — one
// number on the Triage button for everything waiting on the user.
const triageCount = computed((): number =>
  queuedNeedsInput(draft.value, effectiveDraft.value, id => confirmedEventIds.value.has(id))
    .filter(i => !dismissedEventIds.value.has(i.eventId)).length
  + newEventsCount.value,
);

// The button's own label carries its badge and warning: an aria-label on the
// button replaces its content, so the spans inside are never read.
const triageButtonLabel = computed((): string => {
  const parts = ['Triage'];
  if (checkingForNewEvents.value) parts.push('checking for new events');
  else {
    if (triageCount.value > 0) parts.push(`${triageCount.value} to triage`);
    if (newEventsCheckFailed.value) parts.push("couldn't check for new events");
  }
  return parts.join(', ');
});

// Dismiss toggles: a second click on a dismissed card clears the overlay entry
// (the store has no delete, so re-confirming to the row's target is the undo —
// see below). For the icon toggle we re-run dismiss/confirm based on state.
function onDismissEvent(eventId: string): void {
  if (dismissedEventIds.value.has(eventId)) {
    // The store has no per-key delete, so the undo re-confirms the event at its
    // engine target, or re-queues it when it never had one.
    undoDismiss(eventId);
  } else {
    dismissEvent(eventId);
  }
}

// ---------------------------------------------------------------------------
// Quick-triage keyboard (C3) — J/K/C/D/Esc over the inspector's event cards.
// Disabled while any modal/popover is open so keystrokes never leak.
// ---------------------------------------------------------------------------
const attributedEventIds = ref<string[]>([]);
const anyModalOpen = computed(() =>
  cellDetailOpen.value || wizardOpen.value ||
  confirmOpen.value || regenOpen.value || actionsOpen.value,
);
const keyboardInactive = computed(() => !props.eventsOpen);

const { focusedId } = useTriageKeyboard({
  eventIds: () => attributedEventIds.value,
  blockers: [anyModalOpen, keyboardInactive, regenerating],
  onConfirm: confirmEvent,
  onDismiss: onDismissEvent,
});

function goToVerify(): void {
  void router.push('/verify');
}

// ---------------------------------------------------------------------------
// Events-drawer click navigation (Task 6, ux-batch-2026-07) — an attributed
// card scrolls to its grid row; the selection (row outline + cell fill) fades
// in via the grid's box-shadow transition and stays. One signal, no separate
// flash animation to fight it.
// ---------------------------------------------------------------------------
const highlightedCell = ref<{ wbs1: string; dayIndexes: number[]; scope?: 'draft' | 'vp' } | null>(null);

// The cell fill follows the row highlight: toggling the row off (or onto
// another row) drops it.
watch(highlightedWbs, (wbs) => {
  if (!wbs || highlightedCell.value?.wbs1 !== wbs) highlightedCell.value = null;
});

// A wizard add / resolve highlights every cell it wrote with the same selection
// cue as an attributed-event click (row outline + cell fill) and scrolls to it.
// It clears on the next click-away or grid operation, like any other selection.
watch(justAddedRow, (v) => {
  if (!v) return;
  highlightedWbs.value = v.wbs1;
  onScrollToRow(v.wbs1, v.dayIndexes);
  justAddedRow.value = null;
});

// An undo snapshot describes the overlay against one engine draft over one
// period. Both are replaced here, so the offer goes with them rather than
// restoring entries into a sheet that never held them (#6542).
watch([activePeriodStart, () => draft.value], forgetUndo);

function onScrollToRow(wbs1: string, dayIndexes: number[] = [], scope: 'draft' | 'vp' = 'draft'): void {
  highlightScope.value = scope;
  void nextTick(() => {
    // Plain attribute-value comparison rather than a CSS attribute selector —
    // `CSS.escape` isn't available in every test/runtime environment, and wbs1
    // codes can contain characters (., -) that would need escaping there.
    const el = Array.from(document.querySelectorAll<HTMLElement>('[data-row-wbs1]'))
      .find(row => row.getAttribute('data-row-wbs1') === wbs1);
    // jsdom (unit tests) doesn't implement scrollIntoView — guard rather than
    // assume every DOM environment has it.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    highlightedCell.value = dayIndexes.length > 0 ? { wbs1, dayIndexes, scope } : null;
  });
}

</script>
